import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { useAppDispatch } from "../../store";
import { SessionCheckFailure } from "../shared/interfaces/auth-state";
import { startSignIn } from "../shared/start-sign-in";
import {
  getIsAuthenticated,
  getLoadingUserDto,
  getSessionCheckFailure,
  getUserId,
} from "../store/auth.selector";
import { fetchAppUserProfile, initializeAuth } from "../store/auth.slice";

/**
 * Проверяет сессию один раз на маршрут под защитой. Сессии нет — уводит браузер
 * на BFF, который начинает вход в IMS. Отказ и техническую ошибку не превращает
 * во вход: их показывает `ProtectedRoute`, а `retry` проверяет сессию заново.
 */
export function useAuthGate(): {
  isAuthenticated: boolean;
  isLoadingUserDto: boolean;
  sessionCheckFailure: SessionCheckFailure | null;
  retry: () => void;
} {
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isLoadingUserDto = useSelector(getLoadingUserDto);
  const sessionCheckFailure = useSelector(getSessionCheckFailure);
  const userId = useSelector(getUserId);
  const dispatch = useAppDispatch();
  const authCheckStarted = useRef(false);

  const checkSession = useCallback(() => {
    authCheckStarted.current = true;
    dispatch(initializeAuth())
      .unwrap()
      .then((sessionInfo) => {
        if (!sessionInfo.isValid) {
          startSignIn(window.location.href);
        }
      })
      .catch(() => {
        // Причина уже в состоянии (`sessionCheckFailure`), показывает её `ProtectedRoute`.
      });
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated || authCheckStarted.current || isLoadingUserDto) {
      return;
    }
    checkSession();
  }, [isAuthenticated, isLoadingUserDto, checkSession]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchAppUserProfile(userId));
    }
  }, [userId, dispatch]);

  return { isAuthenticated, isLoadingUserDto, sessionCheckFailure, retry: checkSession };
}
